/**
 * Through-geometry x-ray silhouette for units (feature 1, 2026-07-02).
 *
 * Standard MOBA x-ray technique: a second, cheap render pass of a character's
 * SkinnedMesh geometry with `depthFunc: GreaterDepth` + `depthWrite: false`.
 * That combination only draws a fragment where something ALREADY IN the depth
 * buffer is CLOSER than this silhouette — i.e. exactly the pixels where a
 * tree/rock/building occludes the unit. Where the unit is visible normally,
 * its own opaque mesh already wrote a matching (not greater) depth, so the
 * test fails there and the silhouette is invisible — no double-draw, no
 * z-fighting, no interaction with the inverted-hull outline (which renders in
 * the OPAQUE pass, unaffected by this transparent-pass addition).
 *
 * Shares the source's skeleton (SkinnedMesh.bind) so it animates for free —
 * no per-frame bone sync needed, and no per-frame material work either: one
 * MeshBasicMaterial + N SkinnedMesh clones built ONCE at spawn.
 *
 * BUG FIX (2026-07-02, live-play feedback: "x-ray applies at all times"):
 * the GreaterDepth trick alone is NOT sufficient — a character's own geometry
 * constantly self-occludes (an arm in front of a torso, the far side of the
 * head behind the near side, etc.), so the silhouette's farther fragments
 * legitimately have depth greater than the unit's own nearer surfaces already
 * in the buffer. That reads as permanent tint haze over the model even with
 * no world occluder anywhere nearby. Toggling `mesh.visible` purely by the
 * depth trick was never enough — it needs a REAL occlusion signal. This file
 * now also owns a lightweight occlusion-gating registry: every silhouette
 * created here self-registers, and `updateXrayOcclusion()` (called once per
 * frame from main.ts) throttles a camera→mid-body raycast per unit against
 * the world's occluder geometry (doodads/trees/buildings) and only shows the
 * silhouette meshes while that ray is actually blocked, with a short
 * hold/fade so it doesn't flicker at occluder edges. Mirrors the throttled
 * camera→hero probe approach in world/canopy-fade.ts.
 *
 * Used by player.ts (all cubes) and creatures/creature-render.ts (pets only —
 * wild creatures are deliberately excluded, see creature-render.ts). Safe to
 * call on any Object3D: a root with no SkinnedMesh (e.g. the Nightwisp orb
 * pet) just yields an inert no-op handle that never registers for scanning.
 *
 * BUG FIX (2026-07-02, "faded canopy still gates x-ray"): world/canopy-fade.ts
 * separately ghosts whole doodad instances standing between the camera and the
 * HERO. The two systems stack when the hero stands under a canopy: canopy-fade
 * has already dithered the leaves toward transparent, but the x-ray raycast
 * still hits that same (barely-there) mesh and turns the gold silhouette on —
 * so the real model shows through the ghosted leaves WITH a tint overlay on
 * top, exactly the "tinted model" look already rejected once. `isOccluded()`
 * takes an optional `isDoodadFaded` query (wired from main.ts to
 * `canopyFade.isInstanceFaded`) and skips any raycast hit the query reports as
 * already faded, so the silhouette only lights up for occluders actually still
 * solid. This is per-hit-instance, not a blanket "ignore doodads while any
 * fade is active" — canopy-fade's probe only ever targets the hero, so a tree
 * between the camera and a distant ally is never in its fade map and still
 * gates that ally's silhouette normally.
 */
import * as THREE from "three";

const XRAY_OPACITY = 0.45;
const XRAY_RENDER_ORDER = 950; // after all opaque geometry + normal transparent FX

// ── Occlusion gating ────────────────────────────────────────────────────────
const SCAN_INTERVAL_MS = 100;  // ~10Hz per-unit occlusion recheck
const STAGGER_BUCKETS = 6;     // spread up to N units' first scans across the interval
const FADE_TIME_MS = 200;      // fade-out hold after occlusion ends (appear is instant)
const RAY_MARGIN = 0.3;        // must be this much closer than the unit to count as occluding
const MIN_RAY_DIST = 0.5;      // camera essentially at the unit — treat as unoccluded

export interface XraySilhouette {
  /** Re-tint (e.g. once team relation resolves after the local cube arrives). */
  setTint(color: THREE.Color): void;
  dispose(): void;
}

interface Registered {
  root: THREE.Object3D;
  midBodyLocalY: number;
  meshes: THREE.SkinnedMesh[];
  mat: THREE.MeshBasicMaterial;
  scanAccumMs: number;
  current: number; // eased visibility, 0 = fully hidden .. 1 = fully shown
  target: number;  // 0 or 1, set by the latest scan
}

const registry = new Set<Registered>();
let registeredCount = 0;

const _origin = new THREE.Vector3();
const _target = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _box = new THREE.Box3();
const _raycaster = new THREE.Raycaster();

export function addXraySilhouette(root: THREE.Object3D, tint: THREE.Color): XraySilhouette {
  const mat = new THREE.MeshBasicMaterial({
    color: tint,
    transparent: true,
    opacity: XRAY_OPACITY,
    depthTest: true,
    depthWrite: false,
    fog: false,
    toneMapped: false,
  });
  mat.depthFunc = THREE.GreaterDepth;

  type Pending = { parent: THREE.Object3D; mesh: THREE.SkinnedMesh };
  const pending: Pending[] = [];

  root.traverse((node) => {
    const skinned = node as THREE.SkinnedMesh;
    if (!skinned.isSkinnedMesh || !skinned.parent) return;
    const mesh = new THREE.SkinnedMesh(skinned.geometry, mat);
    mesh.name = `${skinned.name || "skinned"}:xray`;
    mesh.bind(skinned.skeleton, skinned.bindMatrix);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.frustumCulled = skinned.frustumCulled;
    mesh.renderOrder = XRAY_RENDER_ORDER;
    // Hidden until the occlusion scan below proves the unit is actually
    // behind something — see the bug-fix note above. Without this gate the
    // GreaterDepth self-occlusion makes the model look permanently tinted.
    mesh.visible = false;
    pending.push({ parent: skinned.parent, mesh });
  });

  // Add after traversal so we don't mutate the tree we're walking.
  for (const { parent, mesh } of pending) parent.add(mesh);

  let entry: Registered | null = null;
  if (pending.length > 0) {
    // Mid-body world-space anchor, derived once from the character's own
    // bounding box (root origin is convention-feet, per player.ts/creature
    // spawn code) — the point the occlusion raycast aims at each scan.
    root.updateMatrixWorld(true);
    _box.setFromObject(root);
    const midY = !_box.isEmpty() ? (_box.min.y + _box.max.y) / 2 - root.position.y : 1.0;

    entry = {
      root,
      midBodyLocalY: midY,
      meshes: pending.map((p) => p.mesh),
      mat,
      // Stagger initial scans across the interval so ≤12 units don't all
      // raycast on the same frame.
      scanAccumMs: (registeredCount % STAGGER_BUCKETS) * (SCAN_INTERVAL_MS / STAGGER_BUCKETS),
      current: 0,
      target: 0,
    };
    registeredCount++;
    registry.add(entry);
  }

  return {
    setTint(color) {
      mat.color.copy(color);
    },
    dispose() {
      if (entry) registry.delete(entry);
      for (const { parent, mesh } of pending) parent.remove(mesh);
      mat.dispose();
    },
  };
}

function isBeaconHit(obj: THREE.Object3D): boolean {
  // Resource drop-beacons (resources.ts `beaconGroup`, name "drop-beacons")
  // are additive translucent VFX, not real occluding geometry — skip them so
  // a beacon standing between the camera and a unit doesn't falsely trigger
  // the silhouette.
  let n: THREE.Object3D | null = obj;
  while (n) {
    if (n.name === "drop-beacons") return true;
    n = n.parent;
  }
  return false;
}

function isOccluded(
  entry: Registered,
  camera: THREE.Camera,
  occluders: readonly THREE.Object3D[],
  isDoodadFaded?: (mesh: THREE.InstancedMesh, instanceId: number) => boolean,
): boolean {
  _origin.copy(camera.position);
  _target.copy(entry.root.position);
  _target.y += entry.midBodyLocalY;
  const dist = _origin.distanceTo(_target);
  if (dist < MIN_RAY_DIST) return false;
  _dir.copy(_target).sub(_origin).multiplyScalar(1 / dist);
  _raycaster.set(_origin, _dir);
  _raycaster.near = 0.1;
  _raycaster.far = dist + RAY_MARGIN;
  // Building flame-glow Sprites (buildings/building-render.ts) sit inside the
  // occluder set; Sprite.raycast needs `.camera` to orient the billboard, or
  // it throws on `null.matrixWorld` — canopy-fade never hits this because its
  // sole occluder root (doodads) has no Sprites.
  _raycaster.camera = camera;
  for (const occluder of occluders) {
    // intersectObject returns hits sorted nearest-first, so once a hit is at
    // or past the unit's own distance, every remaining hit in this array is
    // too — break instead of continue (was scanning the full remainder of
    // dense doodad hit lists for nothing).
    const hits = _raycaster.intersectObject(occluder, true);
    for (const hit of hits) {
      if (hit.distance >= dist - RAY_MARGIN) break;
      if (isBeaconHit(hit.object)) continue;
      // A canopy instance already ghosted by canopy-fade (world/canopy-fade.ts)
      // must not ALSO gate the x-ray silhouette — otherwise the real model
      // shows through the dithered leaves WITH a gold tint stacked on top
      // (rejected look, see file header). Per-hit-instance, not "skip all
      // doodads while any fade is active": a tree between the camera and a
      // DISTANT ally the canopy-fade scan never touched must still occlude.
      if (
        isDoodadFaded &&
        hit.instanceId !== undefined &&
        (hit.object as THREE.InstancedMesh).isInstancedMesh &&
        isDoodadFaded(hit.object as THREE.InstancedMesh, hit.instanceId)
      ) {
        continue;
      }
      return true;
    }
  }
  return false;
}

/**
 * Per-frame occlusion scan + fade, called once from main.ts's frame() loop
 * (independent of the player/creature update loops — every registered
 * silhouette tracks its own root object directly). `occluders` should be the
 * world's occluding geometry roots: doodads (trees/rocks), buildings, and the
 * harvestable resource nodes (resources.ts `.group`) so x-ray keeps working
 * through the resource-pipeline trees it was built for.
 */
export function updateXrayOcclusion(
  dtMs: number,
  camera: THREE.Camera,
  occluders: readonly THREE.Object3D[],
  isDoodadFaded?: (mesh: THREE.InstancedMesh, instanceId: number) => boolean,
): void {
  if (registry.size === 0) return;
  const step = dtMs / FADE_TIME_MS;
  for (const entry of registry) {
    entry.scanAccumMs += dtMs;
    if (entry.scanAccumMs >= SCAN_INTERVAL_MS) {
      entry.scanAccumMs = 0;
      entry.target = occluders.length > 0 && isOccluded(entry, camera, occluders, isDoodadFaded) ? 1 : 0;
    }
    if (entry.target > entry.current) {
      entry.current = entry.target; // appear immediately once occluded
    } else if (entry.current > entry.target) {
      entry.current = Math.max(entry.target, entry.current - step); // ease out
    }
    const visible = entry.current > 0.01;
    if (visible) {
      const t = entry.current;
      entry.mat.opacity = XRAY_OPACITY * (t * t * (3 - 2 * t)); // smoothstep ease
    }
    for (const m of entry.meshes) {
      if (m.visible !== visible) m.visible = visible;
    }
  }
}
